import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Users, Plus, X, Mail, Phone, MapPin, Building2, Euro, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, LEAD_STAGES, timeAgo } from "../../lib/api";

export default function Prospects() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", project: "", budget: "", location: "" });
  const [dragOver, setDragOver] = useState(null);

  const load = () => api.get("/leads").then((r) => setLeads(r.data.leads)).catch(() => {});
  useEffect(() => { load(); }, []);

  const stageLabel = (key) => t(`stages.${key}`);

  const onDrop = async (e, stage) => {
    e.preventDefault();
    setDragOver(null);
    const leadId = e.dataTransfer.getData("lead_id");
    const lead = leads.find((l) => l.lead_id === leadId);
    if (!lead || lead.status === stage) return;
    setLeads(leads.map((l) => l.lead_id === leadId ? { ...l, status: stage } : l));
    await api.patch(`/leads/${leadId}`, { status: stage });
    toast.success(t("prospects.moved", { name: lead.name, stage: stageLabel(stage) }));
  };

  const createLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", form);
      setShowNew(false);
      setForm({ name: "", email: "", phone: "", company: "", project: "", budget: "", location: "" });
      toast.success(t("prospects.added"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const saveNotes = async () => {
    await api.patch(`/leads/${selected.lead_id}`, { notes: selected.notes, next_action: selected.next_action });
    toast.success(t("prospects.updated"));
    load();
  };

  const removeLead = async (id) => {
    await api.delete(`/leads/${id}`);
    setSelected(null);
    toast.success(t("prospects.deleted"));
    load();
  };

  return (
    <div className="space-y-6" data-testid="prospects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tighter">{t("prospects.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("prospects.subtitle")}</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-lead-btn"
          className="rounded-full bg-electric text-white px-5 py-2.5 text-xs font-bold hover:bg-blue-700 transition-colors flex items-center gap-1.5">
          <Plus size={14} /> {t("prospects.new")}
        </button>
      </div>

      {!leads ? (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          {[...Array(7)].map((_, i) => <div key={i} className="skeleton-shimmer h-64 rounded-2xl" />)}
        </div>
      ) : leads.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card py-20 text-center" data-testid="prospects-empty">
          <Users size={32} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-semibold text-sm">{t("prospects.empty1")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("prospects.empty2")}</p>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          {LEAD_STAGES.map((stage) => {
            const items = leads.filter((l) => l.status === stage.key);
            return (
              <div key={stage.key}
                onDragOver={(e) => { e.preventDefault(); setDragOver(stage.key); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => onDrop(e, stage.key)}
                className={`w-56 shrink-0 rounded-2xl border p-3 transition-colors ${dragOver === stage.key ? "border-electric bg-electric/5" : "border-border bg-secondary/40"}`}
                data-testid={`kanban-col-${stage.key}`}>
                <div className="flex items-center justify-between px-1.5 mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{stageLabel(stage.key)}</p>
                  <span className="text-[10px] font-bold bg-background border border-border rounded-full px-2 py-0.5">{items.length}</span>
                </div>
                <div className="space-y-2 min-h-[120px]">
                  {items.map((l) => (
                    <motion.div key={l.lead_id} layout draggable
                      onDragStart={(e) => e.dataTransfer.setData("lead_id", l.lead_id)}
                      onClick={() => setSelected(l)}
                      className="rounded-xl border border-border bg-card p-3.5 cursor-grab active:cursor-grabbing hover:border-electric/40 transition-colors"
                      data-testid={`lead-card-${l.lead_id}`}>
                      <p className="text-xs font-bold truncate">{l.name}</p>
                      {l.company && <p className="text-[10px] text-muted-foreground truncate">{l.company}</p>}
                      <p className="text-[11px] text-muted-foreground mt-1.5 line-clamp-2 leading-snug">{l.project}</p>
                      <div className="flex items-center justify-between mt-2.5">
                        {l.budget
                          ? <span className="text-[10px] font-bold text-success">{l.budget}</span>
                          : <span className="text-[10px] text-muted-foreground">{l.location}</span>}
                        <span className="text-[9px] text-muted-foreground">{timeAgo(l.created_at)}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowNew(false)}>
          <motion.form onClick={(e) => e.stopPropagation()} onSubmit={createLead}
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md rounded-2xl border border-border bg-card p-7 space-y-3" data-testid="new-lead-modal">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-bold text-lg">{t("prospects.modalTitle")}</h2>
              <button type="button" onClick={() => setShowNew(false)} className="p-1.5 rounded-lg hover:bg-secondary"><X size={16} /></button>
            </div>
            {[["name", t("prospects.fields.name")], ["email", t("prospects.fields.email")], ["phone", t("prospects.fields.phone")], ["company", t("prospects.fields.company")], ["project", t("prospects.fields.project")], ["budget", t("prospects.fields.budget")], ["location", t("prospects.fields.location")]].map(([k, ph]) => (
              <input key={k} required={k === "name"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                placeholder={ph} data-testid={`new-lead-${k}`}
                className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow" />
            ))}
            <button type="submit" data-testid="new-lead-submit"
              className="w-full rounded-full bg-electric text-white py-3 text-sm font-bold hover:bg-blue-700 transition-colors">
              {t("prospects.add")}
            </button>
          </motion.form>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <motion.div onClick={(e) => e.stopPropagation()}
            initial={{ x: 420 }} animate={{ x: 0 }} transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="absolute right-0 top-0 h-full w-full max-w-md bg-card border-l border-border p-7 overflow-y-auto"
            data-testid="lead-drawer">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="font-display font-bold text-xl">{selected.name}</h2>
                <p className="text-xs text-muted-foreground mt-1">{t("prospects.stageLabel", { stage: stageLabel(selected.status) })}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-secondary"><X size={16} /></button>
            </div>
            <div className="space-y-2.5 text-sm mb-6">
              {selected.email && <p className="flex items-center gap-2.5 text-muted-foreground"><Mail size={13} /> {selected.email}</p>}
              {selected.phone && <p className="flex items-center gap-2.5 text-muted-foreground"><Phone size={13} /> {selected.phone}</p>}
              {selected.company && <p className="flex items-center gap-2.5 text-muted-foreground"><Building2 size={13} /> {selected.company}</p>}
              {selected.location && <p className="flex items-center gap-2.5 text-muted-foreground"><MapPin size={13} /> {selected.location}</p>}
              {selected.budget && <p className="flex items-center gap-2.5 text-success font-semibold"><Euro size={13} /> {selected.budget}</p>}
            </div>
            <div className="rounded-xl bg-secondary/60 p-4 mb-6">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5">{t("prospects.summaryAi")}</p>
              <p className="text-sm leading-relaxed">{selected.project}</p>
              {selected.notes && <p className="text-xs text-muted-foreground mt-2">{selected.notes}</p>}
            </div>
            {selected.email_ids?.length > 0 && (
              <button onClick={() => navigate(`/app/inbox/${selected.email_ids[0]}`)} data-testid="lead-view-email"
                className="w-full mb-6 rounded-xl border border-border px-4 py-3 text-xs font-semibold hover:border-electric/50 hover:text-electric transition-colors text-left">
                {t("prospects.viewEmail")}
              </button>
            )}
            <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5">{t("prospects.nextAction")}</label>
            <input value={selected.next_action || ""} onChange={(e) => setSelected({ ...selected, next_action: e.target.value })}
              data-testid="lead-next-action"
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-electric mb-4" />
            <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5">{t("prospects.notes")}</label>
            <textarea value={selected.notes || ""} onChange={(e) => setSelected({ ...selected, notes: e.target.value })} rows={4}
              data-testid="lead-notes"
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-electric resize-none mb-6" />
            <div className="flex gap-3">
              <button onClick={saveNotes} data-testid="lead-save"
                className="flex-1 rounded-full bg-electric text-white py-2.5 text-xs font-bold hover:bg-blue-700 transition-colors">{t("common.save")}</button>
              <button onClick={() => removeLead(selected.lead_id)} data-testid="lead-delete"
                className="rounded-full border border-destructive/40 text-destructive px-4 py-2.5 text-xs font-bold hover:bg-destructive/10 transition-colors">
                <Trash2 size={13} />
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
